import { Box, Text, VStack, HStack } from '@chakra-ui/react'
import { Folder, FileText } from 'lucide-react'
import type { DocumentFolderWithRelations } from '../types'
import { SYSTEM_FOLDER_ICONS, SYSTEM_FOLDER_COLORS } from '../config/systemFolderIcons'

interface FolderCardProps {
  folder: DocumentFolderWithRelations
  documentCount?: number
  subfolderCount?: number
  onClick: () => void
}

export function FolderCard({
  folder,
  documentCount = 0,
  subfolderCount = 0,
  onClick,
}: FolderCardProps) {
  // Get system folder icon and color if applicable
  const SystemIcon = SYSTEM_FOLDER_ICONS[folder.slug]
  const systemColor = SYSTEM_FOLDER_COLORS[folder.slug]
  const useSystemIcon = folder.is_system_folder && SystemIcon
  const FolderIcon = useSystemIcon ? SystemIcon : Folder
  const iconColor = useSystemIcon ? systemColor : folder.color ? folder.color : '#718096'

  return (
    <Box
      borderWidth="1px"
      borderRadius="lg"
      p={5}
      bg="white"
      cursor="pointer"
      transition="all 0.2s"
      _hover={{
        borderColor: 'brand.500',
        boxShadow: 'md',
        transform: 'translateY(-2px)',
      }}
      onClick={onClick}
    >
      <VStack align="stretch" gap={3}>
        <HStack gap={3}>
          <Box
            p={2}
            borderRadius="md"
            bg={useSystemIcon ? `${systemColor}15` : folder.color ? `${folder.color}.50` : 'gray.50'}
            borderWidth="1px"
            borderColor={
              useSystemIcon ? `${systemColor}30` : folder.color ? `${folder.color}.100` : 'gray.200'
            }
          >
            <FolderIcon size={20} color={iconColor} />
          </Box>

          <VStack align="start" gap={0} flex={1} minW={0}>
            <Text fontSize="sm" fontWeight="semibold" color="gray.800" lineClamp={2} wordBreak="break-word">
              {folder.name}
            </Text>

            <HStack gap={3} fontSize="xs" color="gray.600" mt={1}>
              {documentCount > 0 && (
                <HStack gap={1}>
                  <FileText size={12} />
                  <Text>{documentCount} doc{documentCount !== 1 ? 's' : ''}</Text>
                </HStack>
              )}
              {subfolderCount > 0 && (
                <HStack gap={1}>
                  <Folder size={12} />
                  <Text>{subfolderCount} dossier{subfolderCount !== 1 ? 's' : ''}</Text>
                </HStack>
              )}
            </HStack>
          </VStack>
        </HStack>
      </VStack>
    </Box>
  )
}
